// src/v3/ui/symbol/symbol-page.tsx

import { isRecord } from "@signals/shared";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AlertTriangle,
  Calculator,
  Pencil,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  DataPage,
  mergePresetLists,
  Modal,
  parseUrlState,
  Progress,
  serializeUrlState,
  SmartEditModal,
  SmartTable,
  ToastContainer,
  useToast,
} from "../_common";
import type {
  ColumnFormatOptions,
  ColumnValueFormat,
  SmartTableColumnMeta,
} from "../_deps";
import {
  AUTO_STATUS_VARIANTS,
  MANUAL_STATUS_VARIANTS,
  partitionFilters,
} from "../_deps";
import { t } from "../locales";
import { symbolPresets } from "../table-presets";
import { SymbolDetail } from "./symbol-detail";
import {
  useDeleteSymbols,
  useProcessSymbols,
  useRecalculateByPrice,
  useSymbols,
  useUpdateSymbol,
  type SymbolItem,
} from "./symbol.hooks";

const SYMBOL_ACTION_OBJ = {
  DELETE: "delete",
} as const;

const RESULT_MESSAGE_RESET_DELAY_MS = 3_000;
const PROGRESS_RESET_DELAY_MS = 2_000;

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (isRecord(acc) && key in acc) {
      return acc[key];
    }
    return undefined;
  }, obj);
}

function setNestedValue(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): void {
  const keys = path.split(".");
  let current: Record<string, unknown> = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]!;
    if (!isRecord(current[key])) {
      current[key] = {};
    }
    const nested = current[key];
    if (!isRecord(nested)) return;
    current = nested;
  }
  const lastKey = keys[keys.length - 1]!;
  current[lastKey] = value;
}

/** Fields that affect price calculations and require recalculateByPrice after save. */
const RECALC_FIELDS = new Set([
  "financials.expectedIncome",
  "financials.expectedIncomeGrowthRate",
]);

function editsToNested(edits: object): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const entries: [string, unknown][] = Object.entries(edits);
  for (const [path, value] of entries) {
    setNestedValue(result, path, value);
  }
  return result;
}

/**
 * Check if any of the edited fields require a price recalculation.
 * Handles both flat dotted keys ("financials.expectedIncome") and
 * nested object keys ({ financials: { expectedIncome: ... } }).
 */
function needsRecalc(fields: object): boolean {
  const entries: [string, unknown][] = Object.entries(fields);
  for (const [key, value] of entries) {
    // Flat dotted key: "financials.expectedIncome"
    if (RECALC_FIELDS.has(key)) return true;
    // Nested object: { financials: { expectedIncome: ... } }
    if (isRecord(value)) {
      for (const subKey of Object.keys(value)) {
        if (RECALC_FIELDS.has(`${key}.${subKey}`)) return true;
      }
    }
  }
  return false;
}

function nestedAccessor(path: string) {
  return (row: SymbolItem) => getNestedValue(row, path);
}

function nestedCol(
  id: string,
  header: string,
  path: string,
  format: ColumnValueFormat,
  formatOptions?: ColumnFormatOptions,
  extra?: Partial<ColumnDef<SymbolItem>>,
): ColumnDef<SymbolItem> {
  const { meta: extraMeta, ...rest } = extra ?? {};
  return {
    id,
    header,
    accessorFn: nestedAccessor(path),
    ...rest,
    meta: {
      format,
      ...(formatOptions !== undefined ? { formatOptions } : {}),
      ...extraMeta,
    } satisfies SmartTableColumnMeta,
  };
}

function nestedText(
  id: string,
  header: string,
  path: string,
  extra?: Partial<ColumnDef<SymbolItem>>,
): ColumnDef<SymbolItem> {
  return {
    id,
    header,
    accessorFn: nestedAccessor(path),
    ...extra,
  };
}

function nestedNum(
  id: string,
  header: string,
  path: string,
  prefix = "",
  extra?: Partial<ColumnDef<SymbolItem>>,
): ColumnDef<SymbolItem> {
  return nestedCol(
    id,
    header,
    path,
    "number",
    prefix ? { prefix } : undefined,
    extra,
  );
}

function nestedPercent(
  id: string,
  header: string,
  path: string,
  extra?: Partial<ColumnDef<SymbolItem>>,
): ColumnDef<SymbolItem> {
  return nestedCol(id, header, path, "percent", { decimals: 2 }, extra);
}

function nestedInt(
  id: string,
  header: string,
  path: string,
  extra?: Partial<ColumnDef<SymbolItem>>,
): ColumnDef<SymbolItem> {
  return nestedCol(id, header, path, "number", { decimals: 0 }, extra);
}

function nestedCurrency(
  id: string,
  header: string,
  path: string,
  extra?: Partial<ColumnDef<SymbolItem>>,
): ColumnDef<SymbolItem> {
  return nestedCol(id, header, path, "currency", undefined, extra);
}

function nestedBool(
  id: string,
  header: string,
  path: string,
  extra?: Partial<ColumnDef<SymbolItem>>,
): ColumnDef<SymbolItem> {
  return nestedCol(id, header, path, "boolean", undefined, extra);
}

export function SymbolPage() {
  const [selected, setSelected] = useState<SymbolItem | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [resultError, setResultError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    symbols: string[];
  } | null>(null);
  const [selectedRows, setSelectedRows] = useState<SymbolItem[]>([]);
  const [skipAi, setSkipAi] = useState(false);
  const [editTarget, setEditTarget] = useState<SymbolItem | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const { toasts, dismissToast, showToast } = useToast();

  const [urlState, setUrlState] = useState(() =>
    parseUrlState(new URLSearchParams(window.location.search)),
  );
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(
    null,
  );
  const [stickyColumns, setStickyColumns] = useState<string[]>(
    urlState.stickyColumns ?? [],
  );
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(
    urlState.hiddenColumns ?? ["timestamp.createdAt"],
  );

  // ── Server-state: TanStack Query ────────────────────────────
  const { serverSide } = useMemo(
    () => partitionFilters(urlState.filters),
    [urlState.filters],
  );
  const symbolsQuery = useSymbols({
    limit: urlState.pageSize,
    offset: (urlState.page - 1) * urlState.pageSize,
    filters: serverSide,
    sort: urlState.sort,
  });
  const items = symbolsQuery.data?.items ?? [];
  const total = symbolsQuery.data?.total ?? 0;
  const isLoading = symbolsQuery.isLoading;
  const isFetching = symbolsQuery.isFetching;
  const error = symbolsQuery.error?.message ?? null;
  const refetch = symbolsQuery.refetch;

  const processSymbolsMutation = useProcessSymbols();
  const recalculateByPriceMutation = useRecalculateByPrice();
  const deleteSymbolsMutation = useDeleteSymbols();
  const updateSymbolMutation = useUpdateSymbol();

  useEffect(() => {
    const newUrl = `${window.location.pathname}${serializeUrlState(urlState)}`;
    window.history.replaceState(null, "", newUrl);
  }, [urlState]);

  useEffect(() => {
    const preset = symbolPresets.find((p) => p.id === urlState.preset);
    setVisibleColumnIds(preset ? preset.columns : null);
    setStickyColumns(preset?.stickyColumns ?? []);
    setHiddenColumns(preset?.hiddenColumns ?? ["timestamp.createdAt"]);
  }, [urlState.preset]);

  useEffect(() => {
    setSelected(null);
  }, [urlState.filters]);

  const clientFilters = useMemo(
    () => partitionFilters(urlState.filters).clientSide,
    [urlState.filters],
  );

  const activePreset = useMemo(
    () => symbolPresets.find((p) => p.id === urlState.preset),
    [urlState.preset],
  );

  const handleOpenEdit = useCallback((row: SymbolItem) => {
    setEditTarget(row);
  }, []);

  const columns: ColumnDef<SymbolItem>[] = useMemo(
    () => [
      {
        id: "apiData.fmp.updatedAt",
        header: "Data Received",
        accessorFn: (row: SymbolItem) => row.apiData?.fmp?.updatedAt,
        meta: { format: "date" } satisfies SmartTableColumnMeta,
      },
      { accessorKey: "symbol", header: "Symbol" },
      { accessorKey: "name", header: "Name" },
      {
        accessorKey: "price",
        header: "Price",
        meta: { format: "currency" } satisfies SmartTableColumnMeta,
      },
      { accessorKey: "exchange", header: "Exchange" },
      {
        accessorKey: "marketCap",
        header: "Market Cap",
        meta: { format: "currency" } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "volume",
        header: "Volume",
        meta: { format: "number" } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "avgVolume",
        header: "Avg Volume",
        meta: { format: "number" } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "sharesOutstanding",
        header: "Shares",
        meta: { format: "number" } satisfies SmartTableColumnMeta,
      },
      {
        id: "previousSymbols",
        header: "Previous Symbols",
        accessorFn: (row: SymbolItem) => row.previousSymbols,
        meta: { format: "array" } satisfies SmartTableColumnMeta,
      },
      {
        id: "updatedAt",
        header: "Updated At",
        accessorFn: (row: SymbolItem) => {
          const ts = row.timestamp;
          if (!ts) return undefined;
          return ts.updatedAt ?? ts.createdAt;
        },
        meta: { format: "date" } satisfies SmartTableColumnMeta,
      },
      nestedText(
        "relations.industry.name",
        "Industry",
        "relations.industry.name",
      ),
      nestedText("relations.country.name", "Country", "relations.country.name"),
      nestedText("relations.company.name", "Company", "relations.company.name"),
      nestedText(
        "relations.exchange.fullName",
        "Exchange (full)",
        "relations.exchange.fullName",
      ),
      nestedText(
        "relations.tradeCurrency.name",
        "Trade Currency",
        "relations.tradeCurrency.name",
      ),
      nestedText(
        "relations.reportedCurrency.name",
        "Reported Currency",
        "relations.reportedCurrency.name",
      ),
      {
        id: "financials.prices.buy.narrow.max",
        header: "Buy Price",
        accessorFn: nestedAccessor("financials.prices.buy.narrow.max"),
        cell: ({ row, getValue }) => {
          const rawValue = getValue();
          const value = typeof rawValue === "number" ? rawValue : null;
          const fallbackValue = nestedAccessor(
            "financials.prices.isNarrowFallback",
          )(row.original);
          const isNarrowFallback =
            typeof fallbackValue === "boolean" ? fallbackValue : false;
          if (value === null || value === undefined || value === 0) {
            return <span className="text-muted-foreground">—</span>;
          }
          const formatted = `$${new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)}`;
          if (isNarrowFallback) {
            return (
              <span
                className="inline-flex items-center gap-1"
                title="Expected Income и Exp Growth Rate не заполнены — показан broad max из Price 1-5"
              >
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                {formatted}
              </span>
            );
          }
          return <span>{formatted}</span>;
        },
        meta: {
          format: "currency",
          customCell: true,
        } satisfies SmartTableColumnMeta,
      },
      nestedCurrency(
        "financials.expectedIncome",
        "Expected Income",
        "financials.expectedIncome",
        { meta: { editable: true } satisfies SmartTableColumnMeta },
      ),
      nestedPercent(
        "financials.expectedIncomeGrowthRate",
        "Exp Growth Rate",
        "financials.expectedIncomeGrowthRate",
        { meta: { editable: true } satisfies SmartTableColumnMeta },
      ),
      nestedCol(
        "financials.incomeType",
        "Income Type",
        "financials.incomeType",
        "chip",
        {
          variants: {
            netIncome: "default",
            cfIncome: "secondary",
          },
        },
        { meta: { editable: true } satisfies SmartTableColumnMeta },
      ),
      {
        accessorKey: "autoStatus",
        header: "Статус: Система",
        meta: {
          editable: false,
          format: "chip",
          formatOptions: {
            variants: AUTO_STATUS_VARIANTS,
          },
        } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "manualStatus",
        header: "Статус: Мой",
        meta: {
          editable: true,
          format: "chip",
          formatOptions: {
            variants: MANUAL_STATUS_VARIANTS,
          },
        } satisfies SmartTableColumnMeta,
      },
      nestedInt("financials.years.sum", "Years Σ", "financials.years.sum"),
      nestedInt(
        "financials.years.earliest",
        "Years Earliest",
        "financials.years.earliest",
      ),
      nestedInt("financials.years.last", "Years Last", "financials.years.last"),
      nestedCurrency(
        "financials.incomeStatement.revenue.sum",
        "Revenue Σ",
        "financials.incomeStatement.revenue.sum",
      ),
      nestedCurrency(
        "financials.incomeStatement.operatingIncome.sum",
        "Op Income Σ",
        "financials.incomeStatement.operatingIncome.sum",
      ),
      nestedCurrency(
        "financials.incomeStatement.netIncome.sum",
        "Net Income Σ",
        "financials.incomeStatement.netIncome.sum",
      ),
      nestedCurrency(
        "financials.incomeStatement.eps.sum",
        "EPS Σ",
        "financials.incomeStatement.eps.sum",
      ),
      nestedPercent(
        "financials.incomeStatement.operationalMargin.avg",
        "Op Margin avg",
        "financials.incomeStatement.operationalMargin.avg",
      ),
      nestedPercent(
        "financials.incomeStatement.netMargin.avg",
        "Net Margin avg",
        "financials.incomeStatement.netMargin.avg",
      ),
      nestedCurrency(
        "financials.balance.totalEquity.sum",
        "Total Equity Σ",
        "financials.balance.totalEquity.sum",
      ),
      nestedCurrency(
        "financials.balance.netDebt.sum",
        "Net Debt Σ",
        "financials.balance.netDebt.sum",
      ),
      nestedNum(
        "financials.balance.debtToEquity.avg",
        "Debt/Equity avg",
        "financials.balance.debtToEquity.avg",
      ),
      nestedCurrency(
        "financials.cashFlow.freeCashFlow.sum",
        "FCF Σ",
        "financials.cashFlow.freeCashFlow.sum",
      ),
      nestedCurrency(
        "financials.cashFlow.FCFPerShare.avg",
        "FCF/share avg",
        "financials.cashFlow.FCFPerShare.avg",
      ),
      nestedPercent(
        "financials.coefs.ROE.avg",
        "ROE avg",
        "financials.coefs.ROE.avg",
      ),
      nestedPercent(
        "financials.coefs.netROE.avg",
        "netROE avg",
        "financials.coefs.netROE.avg",
      ),
      nestedPercent(
        "financials.coefs.ROFCFE.avg",
        "ROFCFE avg",
        "financials.coefs.ROFCFE.avg",
      ),
      nestedPercent(
        "financials.coefs.growthRates.operatingIncome.avg",
        "OpInc Growth avg",
        "financials.coefs.growthRates.operatingIncome.avg",
      ),
      nestedPercent(
        "financials.coefs.growthRates.netIncome.avg",
        "NetInc Growth avg",
        "financials.coefs.growthRates.netIncome.avg",
      ),
      nestedNum(
        "financials.ratios.priceToEarningsRatioTTM",
        "PE TTM",
        "financials.ratios.priceToEarningsRatioTTM",
      ),
      nestedNum(
        "financials.ratios.priceToBookRatioTTM",
        "PB TTM",
        "financials.ratios.priceToBookRatioTTM",
      ),
      nestedPercent(
        "financials.values.discountRate",
        "Discount Rate",
        "financials.values.discountRate",
      ),
      nestedPercent(
        "financials.values.marginOfSafety",
        "Margin of Safety",
        "financials.values.marginOfSafety",
      ),
      nestedNum(
        "financials.values.saleCoefficient",
        "Sale Coef",
        "financials.values.saleCoefficient",
      ),
      nestedPercent(
        "financials.values.expectedIncomeGrowthRate",
        "Exp Inc Growth",
        "financials.values.expectedIncomeGrowthRate",
      ),
      nestedCurrency(
        "financials.prices.fair.broad.min",
        "Fair broad min",
        "financials.prices.fair.broad.min",
      ),
      nestedCurrency(
        "financials.prices.fair.broad.max",
        "Fair broad max",
        "financials.prices.fair.broad.max",
      ),
      nestedCurrency(
        "financials.prices.fair.narrow.min",
        "Fair narrow min",
        "financials.prices.fair.narrow.min",
      ),
      nestedCurrency(
        "financials.prices.fair.narrow.max",
        "Fair narrow max",
        "financials.prices.fair.narrow.max",
      ),
      nestedCurrency(
        "financials.prices.buy.broad.min",
        "Buy broad min",
        "financials.prices.buy.broad.min",
      ),
      nestedCurrency(
        "financials.prices.buy.broad.max",
        "Buy broad max",
        "financials.prices.buy.broad.max",
      ),
      nestedCurrency(
        "financials.prices.sell.broad.min",
        "Sell broad min",
        "financials.prices.sell.broad.min",
      ),
      nestedCurrency(
        "financials.prices.sell.broad.max",
        "Sell broad max",
        "financials.prices.sell.broad.max",
      ),
      nestedBool(
        "financials.flags.isInterestingPrice",
        "Interesting Price",
        "financials.flags.isInterestingPrice",
      ),
      nestedBool(
        "financials.flags.isNoInterestStatus",
        "No Interest",
        "financials.flags.isNoInterestStatus",
      ),
      nestedBool(
        "financials.flags.isInterestingPriceBroadStatus",
        "Interesting Broad",
        "financials.flags.isInterestingPriceBroadStatus",
      ),
      nestedBool(
        "financials.flags.isExpensiveStatus",
        "Expensive",
        "financials.flags.isExpensiveStatus",
      ),
      nestedBool(
        "financials.flags.isVeryExpensiveStatus",
        "Very Expensive",
        "financials.flags.isVeryExpensiveStatus",
      ),
      nestedPercent(
        "financials.dealIncome.perNetIncome",
        "Deal/NetInc",
        "financials.dealIncome.perNetIncome",
      ),
      nestedPercent(
        "financials.dealIncome.perFcf",
        "Deal/FCF",
        "financials.dealIncome.perFcf",
      ),
      nestedPercent(
        "financials.dealIncome.perFcfAndNetIncome",
        "Deal/FCF+NI",
        "financials.dealIncome.perFcfAndNetIncome",
      ),
      nestedText(
        "relations.exchange.key",
        "Exchange",
        "relations.exchange.key",
      ),
      nestedCurrency(
        "financials.incomeStatement.revenue.earliest",
        "Revenue (first)",
        "financials.incomeStatement.revenue.earliest",
      ),
      nestedCurrency(
        "financials.incomeStatement.revenue.last",
        "Revenue (current)",
        "financials.incomeStatement.revenue.last",
      ),
      nestedCurrency(
        "financials.incomeStatement.interestExpense.earliest",
        "Interest Exp (first)",
        "financials.incomeStatement.interestExpense.earliest",
      ),
      nestedCurrency(
        "financials.incomeStatement.interestExpense.last",
        "Interest Exp (current)",
        "financials.incomeStatement.interestExpense.last",
      ),
      nestedCurrency(
        "financials.incomeStatement.interestExpense.sum",
        "Interest Exp Σ",
        "financials.incomeStatement.interestExpense.sum",
      ),
      nestedCurrency(
        "financials.incomeStatement.operatingIncome.earliest",
        "Op Income (first)",
        "financials.incomeStatement.operatingIncome.earliest",
      ),
      nestedCurrency(
        "financials.incomeStatement.operatingIncome.last",
        "Op Income (current)",
        "financials.incomeStatement.operatingIncome.last",
      ),
      nestedCurrency(
        "financials.incomeStatement.netIncome.earliest",
        "Net Income (first)",
        "financials.incomeStatement.netIncome.earliest",
      ),
      nestedCurrency(
        "financials.incomeStatement.netIncome.last",
        "Net Income (current)",
        "financials.incomeStatement.netIncome.last",
      ),
      nestedInt(
        "financials.incomeStatement.weightedAverageShsOutDil.earliest",
        "Avg Shares (first)",
        "financials.incomeStatement.weightedAverageShsOutDil.earliest",
      ),
      nestedInt(
        "financials.incomeStatement.weightedAverageShsOutDil.last",
        "Avg Shares (current)",
        "financials.incomeStatement.weightedAverageShsOutDil.last",
      ),
      nestedInt(
        "financials.incomeStatement.weightedAverageShsOutDil.sum",
        "Avg Shares Σ",
        "financials.incomeStatement.weightedAverageShsOutDil.sum",
      ),
      nestedCurrency(
        "financials.incomeStatement.eps.earliest",
        "EPS (first)",
        "financials.incomeStatement.eps.earliest",
      ),
      nestedCurrency(
        "financials.incomeStatement.eps.last",
        "EPS (current)",
        "financials.incomeStatement.eps.last",
      ),
      nestedCurrency(
        "financials.incomeStatement.operatingIncomePerShare.earliest",
        "OpInc/Share (first)",
        "financials.incomeStatement.operatingIncomePerShare.earliest",
      ),
      nestedCurrency(
        "financials.incomeStatement.operatingIncomePerShare.last",
        "OpInc/Share (current)",
        "financials.incomeStatement.operatingIncomePerShare.last",
      ),
      nestedCurrency(
        "financials.incomeStatement.operatingIncomePerShare.avg",
        "OpInc/Share avg",
        "financials.incomeStatement.operatingIncomePerShare.avg",
      ),
      nestedPercent(
        "financials.incomeStatement.operationalMargin.earliest",
        "Op Margin (first)",
        "financials.incomeStatement.operationalMargin.earliest",
      ),
      nestedPercent(
        "financials.incomeStatement.operationalMargin.last",
        "Op Margin (current)",
        "financials.incomeStatement.operationalMargin.last",
      ),
      nestedCurrency(
        "financials.incomeStatement.netIncomePerShare.earliest",
        "NI/Share (first)",
        "financials.incomeStatement.netIncomePerShare.earliest",
      ),
      nestedCurrency(
        "financials.incomeStatement.netIncomePerShare.last",
        "NI/Share (current)",
        "financials.incomeStatement.netIncomePerShare.last",
      ),
      nestedCurrency(
        "financials.incomeStatement.netIncomePerShare.avg",
        "NI/Share avg",
        "financials.incomeStatement.netIncomePerShare.avg",
      ),
      nestedPercent(
        "financials.incomeStatement.netMargin.earliest",
        "Net Margin (first)",
        "financials.incomeStatement.netMargin.earliest",
      ),
      nestedPercent(
        "financials.incomeStatement.netMargin.last",
        "Net Margin (current)",
        "financials.incomeStatement.netMargin.last",
      ),
      nestedPercent(
        "financials.incomeStatement.interestExpenseToRevenue.earliest",
        "IntExp/Rev (first)",
        "financials.incomeStatement.interestExpenseToRevenue.earliest",
      ),
      nestedPercent(
        "financials.incomeStatement.interestExpenseToRevenue.last",
        "IntExp/Rev (current)",
        "financials.incomeStatement.interestExpenseToRevenue.last",
      ),
      nestedPercent(
        "financials.incomeStatement.interestExpenseToRevenue.avg",
        "IntExp/Rev avg",
        "financials.incomeStatement.interestExpenseToRevenue.avg",
      ),
      nestedPercent(
        "financials.incomeStatement.interestExpenseToOperatingIncome.earliest",
        "IntExp/OpInc (first)",
        "financials.incomeStatement.interestExpenseToOperatingIncome.earliest",
      ),
      nestedPercent(
        "financials.incomeStatement.interestExpenseToOperatingIncome.last",
        "IntExp/OpInc (current)",
        "financials.incomeStatement.interestExpenseToOperatingIncome.last",
      ),
      nestedPercent(
        "financials.incomeStatement.interestExpenseToOperatingIncome.avg",
        "IntExp/OpInc avg",
        "financials.incomeStatement.interestExpenseToOperatingIncome.avg",
      ),
      nestedPercent(
        "financials.incomeStatement.interestExpenseToFCF.earliest",
        "IntExp/FCF (first)",
        "financials.incomeStatement.interestExpenseToFCF.earliest",
      ),
      nestedPercent(
        "financials.incomeStatement.interestExpenseToFCF.last",
        "IntExp/FCF (current)",
        "financials.incomeStatement.interestExpenseToFCF.last",
      ),
      nestedPercent(
        "financials.incomeStatement.interestExpenseToFCF.avg",
        "IntExp/FCF avg",
        "financials.incomeStatement.interestExpenseToFCF.avg",
      ),
      nestedCurrency(
        "financials.balance.netDebt.earliest",
        "Net Debt (first)",
        "financials.balance.netDebt.earliest",
      ),
      nestedCurrency(
        "financials.balance.netDebt.last",
        "Net Debt (current)",
        "financials.balance.netDebt.last",
      ),
      nestedCurrency(
        "financials.balance.totalEquity.earliest",
        "Equity (first)",
        "financials.balance.totalEquity.earliest",
      ),
      nestedCurrency(
        "financials.balance.totalEquity.last",
        "Equity (current)",
        "financials.balance.totalEquity.last",
      ),
      nestedNum(
        "financials.balance.debtToEquity.earliest",
        "Debt/Equity (first)",
        "financials.balance.debtToEquity.earliest",
      ),
      nestedNum(
        "financials.balance.debtToEquity.last",
        "Debt/Equity (current)",
        "financials.balance.debtToEquity.last",
      ),
      nestedCurrency(
        "financials.balance.debtToShares.earliest",
        "Debt/Shares (first)",
        "financials.balance.debtToShares.earliest",
      ),
      nestedCurrency(
        "financials.balance.debtToShares.last",
        "Debt/Shares (current)",
        "financials.balance.debtToShares.last",
      ),
      nestedCurrency(
        "financials.balance.debtToShares.avg",
        "Debt/Shares avg",
        "financials.balance.debtToShares.avg",
      ),
      nestedCurrency(
        "financials.balance.equityToSharesRatio.earliest",
        "Equity/Shares (first)",
        "financials.balance.equityToSharesRatio.earliest",
      ),
      nestedCurrency(
        "financials.balance.equityToSharesRatio.last",
        "Equity/Shares (current)",
        "financials.balance.equityToSharesRatio.last",
      ),
      nestedCurrency(
        "financials.balance.equityToSharesRatio.avg",
        "Equity/Shares avg",
        "financials.balance.equityToSharesRatio.avg",
      ),
      nestedCurrency(
        "financials.cashFlow.freeCashFlow.earliest",
        "FCF (first)",
        "financials.cashFlow.freeCashFlow.earliest",
      ),
      nestedCurrency(
        "financials.cashFlow.freeCashFlow.last",
        "FCF (current)",
        "financials.cashFlow.freeCashFlow.last",
      ),
      nestedCurrency(
        "financials.cashFlow.FCFPerShare.earliest",
        "FCF/Share (first)",
        "financials.cashFlow.FCFPerShare.earliest",
      ),
      nestedCurrency(
        "financials.cashFlow.FCFPerShare.last",
        "FCF/Share (current)",
        "financials.cashFlow.FCFPerShare.last",
      ),
      nestedPercent(
        "financials.coefs.ROE.earliest",
        "ROE (first)",
        "financials.coefs.ROE.earliest",
      ),
      nestedPercent(
        "financials.coefs.ROE.last",
        "ROE (current)",
        "financials.coefs.ROE.last",
      ),
      nestedPercent(
        "financials.coefs.ROEPriceAdapted.avg",
        "ROE Price Adj avg",
        "financials.coefs.ROEPriceAdapted.avg",
      ),
      nestedPercent(
        "financials.coefs.netROEPriceAdapted.avg",
        "Net ROE Price Adj avg",
        "financials.coefs.netROEPriceAdapted.avg",
      ),
      nestedPercent(
        "financials.coefs.ROFCFE.earliest",
        "ROFCFE (first)",
        "financials.coefs.ROFCFE.earliest",
      ),
      nestedPercent(
        "financials.coefs.ROFCFE.last",
        "ROFCFE (current)",
        "financials.coefs.ROFCFE.last",
      ),
      nestedPercent(
        "financials.coefs.netROFCFE.avg",
        "Net ROFCFE avg",
        "financials.coefs.netROFCFE.avg",
      ),
      nestedPercent(
        "financials.coefs.ROFCFEPriceAdapted.avg",
        "ROFCFE Price Adj avg",
        "financials.coefs.ROFCFEPriceAdapted.avg",
      ),
      nestedPercent(
        "financials.coefs.netROFCFEPriceAdapted.avg",
        "Net ROFCFE Price Adj avg",
        "financials.coefs.netROFCFEPriceAdapted.avg",
      ),
      nestedPercent(
        "financials.coefs.growthRates.netIncomePerShare.avg",
        "NI/Share Growth avg",
        "financials.coefs.growthRates.netIncomePerShare.avg",
      ),
      nestedPercent(
        "financials.coefs.growthRates.FCFPerShare.avg",
        "FCF/Share Growth avg",
        "financials.coefs.growthRates.FCFPerShare.avg",
      ),
      nestedCurrency(
        "financials.prices.fair.price1",
        "Fair Price 1",
        "financials.prices.fair.price1",
      ),
      nestedCurrency(
        "financials.prices.fair.price2",
        "Fair Price 2",
        "financials.prices.fair.price2",
      ),
      nestedCurrency(
        "financials.prices.fair.price3",
        "Fair Price 3",
        "financials.prices.fair.price3",
      ),
      nestedCurrency(
        "financials.prices.fair.price4",
        "Fair Price 4",
        "financials.prices.fair.price4",
      ),
      nestedCurrency(
        "financials.prices.fair.price5",
        "Fair Price 5",
        "financials.prices.fair.price5",
      ),
      nestedCurrency(
        "financials.prices.fair.price6",
        "Fair Price 6",
        "financials.prices.fair.price6",
      ),
      nestedCurrency(
        "financials.prices.fair.price7",
        "Fair Price 7",
        "financials.prices.fair.price7",
      ),
      nestedCurrency(
        "financials.prices.fair.price8",
        "Fair Price 8",
        "financials.prices.fair.price8",
      ),
      nestedCurrency(
        "financials.prices.buy.price1",
        "Buy Price 1",
        "financials.prices.buy.price1",
      ),
      nestedCurrency(
        "financials.prices.buy.price2",
        "Buy Price 2",
        "financials.prices.buy.price2",
      ),
      nestedCurrency(
        "financials.prices.buy.price3",
        "Buy Price 3",
        "financials.prices.buy.price3",
      ),
      nestedCurrency(
        "financials.prices.buy.price4",
        "Buy Price 4",
        "financials.prices.buy.price4",
      ),
      nestedCurrency(
        "financials.prices.buy.price5",
        "Buy Price 5",
        "financials.prices.buy.price5",
      ),
      nestedCurrency(
        "financials.prices.buy.price6",
        "Buy Price 6",
        "financials.prices.buy.price6",
      ),
      nestedCurrency(
        "financials.prices.buy.price7",
        "Buy Price 7",
        "financials.prices.buy.price7",
      ),
      nestedCurrency(
        "financials.prices.buy.price8",
        "Buy Price 8",
        "financials.prices.buy.price8",
      ),
      nestedCurrency(
        "financials.prices.sell.price1",
        "Sell Price 1",
        "financials.prices.sell.price1",
      ),
      nestedCurrency(
        "financials.prices.sell.price2",
        "Sell Price 2",
        "financials.prices.sell.price2",
      ),
      nestedCurrency(
        "financials.prices.sell.price3",
        "Sell Price 3",
        "financials.prices.sell.price3",
      ),
      nestedCurrency(
        "financials.prices.sell.price4",
        "Sell Price 4",
        "financials.prices.sell.price4",
      ),
      nestedCurrency(
        "financials.prices.sell.price5",
        "Sell Price 5",
        "financials.prices.sell.price5",
      ),
      nestedCurrency(
        "financials.prices.sell.price6",
        "Sell Price 6",
        "financials.prices.sell.price6",
      ),
      nestedCurrency(
        "financials.prices.sell.price7",
        "Sell Price 7",
        "financials.prices.sell.price7",
      ),
      nestedCurrency(
        "financials.prices.sell.price8",
        "Sell Price 8",
        "financials.prices.sell.price8",
      ),
      nestedCurrency(
        "financials.prices.buy.narrow.min",
        "Buy narrow min",
        "financials.prices.buy.narrow.min",
      ),
      nestedCurrency(
        "financials.prices.sell.narrow.min",
        "Sell narrow min",
        "financials.prices.sell.narrow.min",
      ),
      nestedCurrency(
        "financials.prices.sell.narrow.max",
        "Sell narrow max",
        "financials.prices.sell.narrow.max",
      ),
      nestedText(
        "apiData.fmp.companyOutlook.profile.companyName",
        "Company Name",
        "apiData.fmp.companyOutlook.profile.companyName",
      ),
      nestedText(
        "apiData.fmp.companyOutlook.profile.description",
        "Description",
        "apiData.fmp.companyOutlook.profile.description",
      ),
      nestedText(
        "apiData.fmp.companyOutlook.profile.industry",
        "FMP Industry",
        "apiData.fmp.companyOutlook.profile.industry",
      ),
      nestedText(
        "apiData.fmp.companyOutlook.profile.country",
        "FMP Country",
        "apiData.fmp.companyOutlook.profile.country",
      ),
      nestedText(
        "apiData.fmp.companyOutlook.profile.currency",
        "Currency",
        "apiData.fmp.companyOutlook.profile.currency",
      ),
      {
        accessorKey: "id",
        header: "ID",
        meta: { format: "id" } satisfies SmartTableColumnMeta,
      },
      {
        id: "timestamp.createdAt",
        accessorFn: (row: SymbolItem) => row.timestamp?.createdAt,
        header: "Created At",
        meta: { format: "datetime" } satisfies SmartTableColumnMeta,
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          filterable: "none",
          sortable: "none",
          isAction: true,
        } satisfies SmartTableColumnMeta,
        cell: ({ row }) => {
          const sym = row.original.symbol;
          return (
            <div className="flex gap-0.5">
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 border-blue-400 text-blue-600 hover:bg-blue-50 p-0"
                aria-label="Edit"
                title="Edit"
                disabled={actionLoading !== null}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEdit(row.original);
                }}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 border-emerald-400 text-emerald-600 hover:bg-emerald-50 p-0"
                aria-label={t("symbol.action.aria.process", { symbol: sym })}
                title={t("symbol.action.aria.process", { symbol: sym })}
                disabled={actionLoading !== null}
                onClick={async (e) => {
                  e.stopPropagation();
                  setActionLoading(`process-${sym}`);
                  setResultError(null);
                  setResultMessage(null);
                  try {
                    await processSymbolsMutation.mutateAsync({
                      symbols: [sym],
                      skipAi,
                    });
                    setResultMessage(t("symbol.result.success"));
                  } catch (err) {
                    setResultError(
                      t("symbol.result.error", {
                        message: err instanceof Error ? err.message : "error",
                      }),
                    );
                  } finally {
                    setActionLoading(null);
                    setTimeout(
                      () => setResultMessage(null),
                      RESULT_MESSAGE_RESET_DELAY_MS,
                    );
                    void refetch();
                  }
                }}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 border-amber-400 text-amber-600 hover:bg-amber-50 p-0"
                aria-label={t("symbol.action.aria.recalculate", {
                  symbol: sym,
                })}
                title={t("symbol.action.aria.recalculate", {
                  symbol: sym,
                })}
                disabled={actionLoading !== null}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (row.original.price === null) return;
                  setActionLoading(`recalculate-${sym}`);
                  setResultError(null);
                  setResultMessage(null);
                  try {
                    await recalculateByPriceMutation.mutateAsync([
                      {
                        symbol: sym,
                        price: row.original.price,
                      },
                    ]);
                    setResultMessage(t("symbol.result.success"));
                  } catch (err) {
                    setResultError(
                      t("symbol.result.error", {
                        message: err instanceof Error ? err.message : "error",
                      }),
                    );
                  } finally {
                    setActionLoading(null);
                    setTimeout(
                      () => setResultMessage(null),
                      RESULT_MESSAGE_RESET_DELAY_MS,
                    );
                    void refetch();
                  }
                }}
              >
                <Calculator className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 border-red-500 text-red-600 hover:bg-red-50 p-0"
                aria-label={t("symbol.action.aria.delete", { symbol: sym })}
                title={t("symbol.action.aria.delete", { symbol: sym })}
                disabled={actionLoading !== null}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDelete({ symbols: [sym] });
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ],
    [
      actionLoading,
      skipAi,
      refetch,
      handleOpenEdit,
      processSymbolsMutation,
      recalculateByPriceMutation,
    ],
  );

  const handlePresetChange = (presetId: string) => {
    const preset = symbolPresets.find((p) => p.id === presetId);
    if (preset) {
      setUrlState({
        page: 1,
        pageSize: preset.pageSize,
        filters: preset.filters,
        sort: preset.sort,
        preset: presetId,
      });
    }
  };

  const handleBulkSave = useCallback(
    async (edits: Map<string, Partial<SymbolItem>>) => {
      const promises = Array.from(edits.entries()).map(([id, fields]) => {
        const nestedBody = editsToNested(fields);
        return updateSymbolMutation.mutateAsync({
          id: encodeURIComponent(id),
          body: nestedBody,
        });
      });
      if (promises.length > 0) {
        try {
          await Promise.all(promises);
          showToast(
            "success",
            t("table.edit.saveSuccess", {
              field: `${promises.length} symbols`,
              symbol: "",
            }),
          );
        } catch (err) {
          const message = err instanceof Error ? err.message : "error";
          showToast(
            "error",
            t("table.edit.saveError", { field: "bulk", message }),
          );
          showToast("info", t("table.edit.reverted"));
        }
      }
      // Collect affected symbols for price recalculation
      const recalcItems: { symbol: string; price: number }[] = [];
      for (const [id, fields] of edits.entries()) {
        if (!needsRecalc(fields)) continue;
        const item = items.find((i) => i.id === id);
        if (item && item.price !== null && item.price !== undefined) {
          recalcItems.push({
            symbol: item.symbol,
            price: item.price,
          });
        }
      }
      if (recalcItems.length > 0) {
        try {
          await recalculateByPriceMutation.mutateAsync(recalcItems);
        } catch {
          // recalculate failed — data still saved, just no price update
        }
      }
      void refetch();
    },
    [
      refetch,
      showToast,
      recalculateByPriceMutation,
      items,
      updateSymbolMutation,
    ],
  );

  const handleInlineEdit = useCallback(
    async (rowId: string, field: string, value: unknown) => {
      const item = items.find((i) => i.id === rowId);
      if (!item) return;
      try {
        const nestedBody = editsToNested({
          [field]: value,
        });
        await updateSymbolMutation.mutateAsync({
          id: encodeURIComponent(rowId),
          body: nestedBody,
        });
        showToast(
          "success",
          t("table.edit.saveSuccess", { field, symbol: item.symbol }),
        );
        // If expected income / growth rate changed, recalculate prices
        if (
          needsRecalc({ [field]: value }) &&
          item.price !== null &&
          item.price !== undefined
        ) {
          try {
            await recalculateByPriceMutation.mutateAsync([
              { symbol: item.symbol, price: item.price },
            ]);
          } catch {
            // recalculate failed — data still saved, just no price update
          }
        }
        void refetch();
      } catch (err) {
        const message = err instanceof Error ? err.message : "error";
        showToast("error", t("table.edit.saveError", { field, message }));
        showToast("info", t("table.edit.reverted"));
        void refetch();
      }
    },
    [
      items,
      refetch,
      showToast,
      recalculateByPriceMutation,
      updateSymbolMutation,
    ],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmDelete) return;
    setActionLoading(SYMBOL_ACTION_OBJ.DELETE);
    setResultError(null);
    setResultMessage(null);
    try {
      await deleteSymbolsMutation.mutateAsync(confirmDelete.symbols);
      setResultMessage(
        t("symbol.result.deleteSuccess", {
          count: confirmDelete.symbols.length,
        }),
      );
    } catch (err) {
      setResultError(
        t("symbol.result.error", {
          message: err instanceof Error ? err.message : "error",
        }),
      );
    } finally {
      setActionLoading(null);
      setConfirmDelete(null);
      setTimeout(() => setResultMessage(null), RESULT_MESSAGE_RESET_DELAY_MS);
      void refetch();
    }
  }, [confirmDelete, deleteSymbolsMutation, refetch]);

  const handleProcessSelected = useCallback(async () => {
    const symbols = selectedRows.map((r) => r.symbol);
    if (symbols.length === 0) return;
    setActionLoading("process-selected");
    setResultError(null);
    setResultMessage(null);
    try {
      await processSymbolsMutation.mutateAsync({ symbols, skipAi });
      setResultMessage(t("symbol.result.success"));
    } catch (err) {
      setResultError(
        t("symbol.result.error", {
          message: err instanceof Error ? err.message : "error",
        }),
      );
    } finally {
      setActionLoading(null);
      setTimeout(() => setResultMessage(null), RESULT_MESSAGE_RESET_DELAY_MS);
      void refetch();
    }
  }, [selectedRows, processSymbolsMutation, refetch, skipAi]);

  const handleRecalculateSelected = useCallback(async () => {
    const items = selectedRows.filter(
      (r) => r.price !== null && r.price !== undefined,
    );
    if (items.length === 0) return;
    setActionLoading("recalculate-selected");
    setResultError(null);
    setResultMessage(null);
    try {
      await recalculateByPriceMutation.mutateAsync(
        items.map((r) => ({
          symbol: r.symbol,
          price: r.price!,
        })),
      );
      setResultMessage(t("symbol.result.success"));
    } catch (err) {
      setResultError(
        t("symbol.result.error", {
          message: err instanceof Error ? err.message : "error",
        }),
      );
    } finally {
      setActionLoading(null);
      setTimeout(() => setResultMessage(null), RESULT_MESSAGE_RESET_DELAY_MS);
      void refetch();
    }
  }, [selectedRows, recalculateByPriceMutation, refetch]);

  const handleDeleteSelected = useCallback(() => {
    const symbols = selectedRows.map((r) => r.symbol);
    if (symbols.length === 0) return;
    setConfirmDelete({ symbols });
  }, [selectedRows]);

  const handleSaveEdit = useCallback(
    async (values: Partial<SymbolItem>) => {
      if (!editTarget) return;
      setIsSavingEdit(true);
      try {
        await updateSymbolMutation.mutateAsync({
          id: encodeURIComponent(editTarget.id),
          body: Object.fromEntries(Object.entries(values)),
        });
        showToast(
          "success",
          t("table.edit.saveSuccess", {
            field: Object.keys(values).join(", "),
            symbol: editTarget.symbol,
          }),
        );
        // If expected income / growth rate changed, recalculate prices
        if (
          needsRecalc(values) &&
          editTarget.price !== null &&
          editTarget.price !== undefined
        ) {
          try {
            await recalculateByPriceMutation.mutateAsync([
              { symbol: editTarget.symbol, price: editTarget.price },
            ]);
          } catch {
            // recalculate failed — data still saved, just no price update
          }
        }
        setEditTarget(null);
        void refetch();
      } catch (err) {
        const message = err instanceof Error ? err.message : "error";
        showToast(
          "error",
          t("table.edit.saveError", { field: "edit", message }),
        );
      } finally {
        setIsSavingEdit(false);
      }
    },
    [
      editTarget,
      refetch,
      showToast,
      recalculateByPriceMutation,
      updateSymbolMutation,
    ],
  );

  return (
    <div className="space-y-4">
      <DataPage
        title="Symbols"
        isLoading={isLoading && items.length === 0}
        error={error}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground select-none">
              <input
                type="checkbox"
                checked={skipAi}
                onChange={(e) => setSkipAi(e.target.checked)}
                className="h-3.5 w-3.5"
              />
              {t("symbol.action.skipAi")}
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                setProgress(0);
                await processSymbolsMutation.mutateAsync({
                  symbols: items.map((i) => i.symbol),
                  skipAi,
                });
                setProgress(100);
                setTimeout(() => setProgress(null), PROGRESS_RESET_DELAY_MS);
                void refetch();
              }}
            >
              Process All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                setProgress(0);
                await recalculateByPriceMutation.mutateAsync(
                  items
                    .filter((i) => i.price !== null && i.price !== undefined)
                    .map((i) => ({
                      symbol: i.symbol,
                      price: i.price!,
                    })),
                );
                setProgress(100);
                setTimeout(() => setProgress(null), PROGRESS_RESET_DELAY_MS);
                void refetch();
              }}
            >
              <Calculator className="mr-2 h-4 w-4" />
              Recalculate All
            </Button>
          </>
        }
      >
        {resultMessage && (
          <div className="rounded-md border border-green-400/40 bg-green-50 p-3 text-sm text-green-700">
            {resultMessage}
          </div>
        )}
        {resultError && (
          <div className="rounded-md border border-red-400/40 bg-red-50 p-3 text-sm text-red-700">
            {resultError}
          </div>
        )}
        <SmartTable
          columns={columns}
          data={items}
          total={total}
          page={urlState.page}
          pageSize={urlState.pageSize}
          isLoading={isFetching}
          error={null}
          filters={urlState.filters}
          sort={urlState.sort}
          clientFilters={clientFilters}
          presets={symbolPresets}
          activePresetId={urlState.preset}
          visibleColumnIds={visibleColumnIds}
          stickyColumns={stickyColumns}
          hiddenColumns={hiddenColumns}
          columnGroups={activePreset?.columnGroups ?? []}
          columnMeta={activePreset?.columnMeta ?? {}}
          lists={mergePresetLists(symbolPresets, urlState.preset)}
          enableBulkEdit
          onBulkSave={handleBulkSave}
          onInlineEdit={handleInlineEdit}
          onSelectedRowsChange={setSelectedRows}
          bulkActions={
            <>
              <Button
                variant="outline"
                size="sm"
                aria-label={t("symbol.bulkEdit.aria.processSelected")}
                disabled={actionLoading !== null}
                onClick={handleProcessSelected}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {t("symbol.bulkEdit.processSelected")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                aria-label={t("symbol.bulkEdit.aria.recalculateSelected")}
                disabled={actionLoading !== null}
                onClick={handleRecalculateSelected}
              >
                <Calculator className="mr-2 h-4 w-4" />
                {t("symbol.bulkEdit.recalculateSelected")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-red-400 text-red-600 hover:bg-red-50"
                aria-label={t("symbol.bulkEdit.aria.deleteSelected")}
                disabled={actionLoading !== null}
                onClick={handleDeleteSelected}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("symbol.bulkEdit.deleteSelected")}
              </Button>
            </>
          }
          onVisibleColumnsChange={(ids) => setVisibleColumnIds(ids)}
          onPageChange={(page) => setUrlState((prev) => ({ ...prev, page }))}
          onPageSizeChange={(pageSize) =>
            setUrlState((prev) => ({ ...prev, pageSize, page: 1 }))
          }
          onFiltersChange={(filters) =>
            setUrlState((prev) => ({ ...prev, filters, page: 1 }))
          }
          onSortChange={(sort) => setUrlState((prev) => ({ ...prev, sort }))}
          onPresetChange={handlePresetChange}
          onRowClick={(row) => setSelected(row)}
        />
        {progress !== null && (
          <Progress value={progress} label="Processing..." />
        )}
        {actionLoading !== null && (
          <Progress value={50} label={actionLoading} />
        )}
      </DataPage>
      <Modal
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected !== null ? selected.symbol : undefined}
        maxWidth="max-w-5xl"
      >
        {selected !== null && <SymbolDetail row={selected} />}
      </Modal>
      <SmartEditModal
        row={editTarget}
        columns={columns}
        lists={mergePresetLists(symbolPresets, urlState.preset)}
        open={editTarget !== null}
        isSaving={isSavingEdit}
        onClose={() => setEditTarget(null)}
        onSave={handleSaveEdit}
        titleField="symbol"
      />
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      {confirmDelete !== null && (
        <div
          role="alertdialog"
          aria-label={t("symbol.delete.aria.confirmDialog")}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        >
          <div className="rounded-lg border border-red-400/40 bg-background p-6 shadow-lg max-w-md">
            <h2 className="text-lg font-semibold mb-2">
              {t("symbol.delete.confirmTitle")}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t("symbol.delete.confirmMessage", {
                count: confirmDelete.symbols.length,
              })}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                aria-label={t("symbol.delete.aria.cancelButton")}
                onClick={() => setConfirmDelete(null)}
              >
                {t("symbol.delete.confirmCancel")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-red-500 text-red-600 hover:bg-red-50"
                aria-label={t("symbol.delete.aria.confirmButton")}
                disabled={actionLoading !== null}
                onClick={handleConfirmDelete}
              >
                {actionLoading === SYMBOL_ACTION_OBJ.DELETE
                  ? t("symbol.delete.confirmLoading")
                  : t("symbol.delete.confirmConfirm")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
